import React, { useEffect, useState } from "react";
import { ColumnConfig, Waffles } from "@app/shared/waffles";
import { QESButton, QESSelect } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { JobStatisticsByLineOfBusinessResult } from "./jobStatisticsResult";
import OperationsService from "./operationsService";
import { FlyoutLink } from "@app/shared/Flyouts";
import UnfinishedProjectsFlyoutContent from "./UnfinishedProjectsFlyoutContent";
import { FullJobDetailsFlyoutContent } from "./FullJobDetailsFlyoutContent";
import { LoadingSpinner, TooltipWrapper } from "@app/shared";

const operationsService = new OperationsService();
const ALL_LINES_OF_BUSINESS = "all";
const ALL_JOB_TYPES = 0;
const JOB_TYPES = [
  { label: "1001 - Custom Adequacy", value: 1001 },
  { label: "1002 - Custom Adequacy Region Summary", value: 1002 },
];

const projectStatisticsColumns: ColumnConfig = {
  lineOfBusiness: {
    displayText: "Line Of Business",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__unfinished-project-line-of-business-output",
  },
  projectCount: {
    displayText: "Project Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__unfinished-project-count-output",
  },
};

const jobStatisticsColumns: ColumnConfig = {
  jobCount: {
    displayText: "Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-count-output",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-type-output",
  },
  lineOfBusiness: {
    displayText: "Line Of Business",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__line-of-business-output",
  },
  status: {
    displayText: "Status",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__status-output",
  },
};

const getMostRecentWednesday = (): string =>
  (d => (d.setDate(d.getDate() - (d.getDay() + 4) % 7), d.toISOString().split("T")[0]))(new Date());

interface JobStatisticsByLineOfBusinessProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const JobStatisticsByLineOfBusiness: React.FC<JobStatisticsByLineOfBusinessProps> = ({
  clients,
  clientId,
  setClientId,
}) => {
  const [data, setData] = useState<JobStatisticsByLineOfBusinessResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(getMostRecentWednesday());
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [lineOfBusiness, setLineOfBusiness] = useState<string>(ALL_LINES_OF_BUSINESS);
  const [jobType, setJobType] = useState<number>(ALL_JOB_TYPES);
  const [fetchLocked, setFetchLocked] = useState<boolean>(false);

  useEffect(() => {
    resetFormValues();
    if (clientId == null) {
      return;
    }

    operationsService
      .getLinesOfBusiness(clientId)
      .then(linesOfBusiness => setLineOfBusinessOptions(linesOfBusiness));
  }, [clientId]);

  const fetchData = async () => {
    setData(null);
    setLoading(true);
    setError(null);
    setFetchLocked(true);

    try {
      const adjustedLineOfBusiness = lineOfBusiness === ALL_LINES_OF_BUSINESS ? "" : lineOfBusiness;
      const result = await operationsService.getJobStatisticsByLineOfBusiness(
        clientId,
        runDate,
        adjustedLineOfBusiness,
        jobType
      );
      setData(result);
    } catch (err) {
      setError("Error fetching job statistics by Line of Business");
    } finally {
      setLoading(false);
    }
  };

  const resetFormValues = () => {
    setError(null);
    setLineOfBusinessOptions([]);
    setLineOfBusiness(ALL_LINES_OF_BUSINESS);
    setJobType(ALL_JOB_TYPES);
    setData(null);
    setFetchLocked(false);
  };

  function handleFilterChange<T>(setMethod: React.Dispatch<React.SetStateAction<T>>, newValue: T): void {
    setMethod(oldValue => {
      if (oldValue != newValue) {
        setFetchLocked(false);
      }
      return newValue;
    });
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col">
          <QESSelect
            label="Client"
            value={clientId !== null ? clientId.toString() : ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            testId="weekend-run-monitoring__job-statistics-by-line-of-business-client-select"
            placeholder="Select Client"
            required
            disabled={clients.length === 0}
            descriptiveText={clients.length === 0 ? "Loading Clients..." : ""}
            isValid={clientId !== null}
          >
            {clients.map(client => (
              <option key={client.clientId} value={client.clientId}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            label="Line of Business"
            value={lineOfBusiness}
            onChange={e => handleFilterChange(setLineOfBusiness, e.target.value)}
            testId="weekend-run-monitoring__line-of-business-select"
            disabled={clientId === null}
          >
            <option value={ALL_LINES_OF_BUSINESS}>All</option>
            {lineOfBusinessOptions.map(lob => (
              <option key={lob.toLowerCase()} value={lob.toLowerCase()}>
                {lob.toUpperCase()}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            label="Job Type"
            value={jobType}
            onChange={e => handleFilterChange(setJobType, Number(e.target.value))}
            testId="weekend-run-monitoring__job-type-select"
            disabled={clientId === null}
          >
            <option value={ALL_JOB_TYPES}>All</option>
            {JOB_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={e => handleFilterChange(setRunDate, e.target.value)}
            testId="weekend-run-monitoring__run-date-input"
          />
        </div>
        <div className="col d-flex align-items-end">
          <TooltipWrapper position="top" text={clientId === null ? "Please select a client." : (fetchLocked ? "Change the filters to calculate new results." : "")}>
            <QESButton onClick={fetchData} qesType="primary" qesTestId="weekend-run-monitoring__calculate-job-statistics-button" disabled={clientId === null || fetchLocked}>
              Calculate Results
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      {loading && <LoadingSpinner/>}
      {error && <div>{error}</div>}
      {data && (
        <div className="row">
          <div className="col-lg-4 mb-4">
            <h5 className="font-weight-normal">Unfinished Projects</h5>
            <Waffles
              columns={projectStatisticsColumns}
              data={data.projectStatistics.map((stat, index) => ({
                key: index.toString(),
                lineOfBusiness: stat.lineOfBusiness.toUpperCase(),
                projectCount: (
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                      body: (
                        <UnfinishedProjectsFlyoutContent
                          runDate={runDate}
                          clientId={clientId}
                          lineOfBusiness={stat.lineOfBusiness}
                        />
                      ),
                      title: `Unfinished Projects for ${stat.lineOfBusiness}`,
                    }}
                    testId={`weekend-run-monitoring__unfinished-projects__flyout-${index}`}
                    size="60"
                  >
                    {stat.projectCount}
                  </FlyoutLink>
                ),
              }))}
              enablePagination={false}
              passThroughSorting={true}
              testId="weekend-run-monitoring__unfinished-projects-table"
            />
          </div>
          <div className="col-8">
            <h5 className="font-weight-normal">Job Statistics</h5>
            <Waffles
              /* eslint-disable sort-keys */
              columns={jobStatisticsColumns}
              data={data.jobStatistics.map((stat, index) => ({
                key: index.toString(),
                status: (
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                      title: `Full Job Details for ${stat.lineOfBusiness}`,
                      body: (
                        <FullJobDetailsFlyoutContent
                          clientId={clientId}
                          runDate={runDate}
                          jobStatus={stat.status}
                          lineOfBusiness={stat.lineOfBusiness}
                          jobType={stat.jobType}
                        />
                      ),
                    }}
                    testId={`weekend-run-monitoring__full-job-details-flyout-${index}`}
                    size="80"
                  >
                    {stat.status}
                  </FlyoutLink>
                ),
                lineOfBusiness: stat.lineOfBusiness,
                jobType: stat.jobType,
                jobCount: stat.jobCount,
              }))}
              enablePagination={false}
              passThroughSorting={true}
              testId="weekend-run-monitoring__job-statistics-by-line-of-business-table"
            /* eslint-enable sort-keys */
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobStatisticsByLineOfBusiness;